import "core-js";
import { createBrowserHistory } from "history";
import "media-match/media.match";
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router";
import { createBrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";
import Alert from "react-s-alert";
import { routes } from "./routes";
import { OverlayLoading } from "./components/common";
import { rootEpic } from "./epics/";
import { AppSettings, setAppSettings } from "./helpers/AppSettings";
import reducers from "./reducers/";
import { configureStore } from "./store/";
import { AuthenticatedUserProvider } from "./providers/AuthenticatedUserProvider";

import "./styles/app.scss";
import { createSentryAnonymousUser } from "./helpers/errors";

// Initialize Sentry monitoring
const initializeSentry = () => {
    if (!AppSettings.sentry.dsn) {
        console.log('[Sentry] Skipping initialization - no DSN configured');
        return;
    }

    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];

    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }

    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
          'localhost', 
          /^\/$/,
          new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });

    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());

    console.log('[Sentry] Initialized successfully');
};

// Initialize Sentry immediately if AppSettings are available (production/server-rendered)
if (AppSettings && AppSettings.sentry?.dsn) {
    initializeSentry();
}

const reducer = reducers;

export const browserHistory = createBrowserHistory();

export const store = configureStore(reducer, rootEpic, browserHistory);

const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(routes as any);

const App: React.FC = () => {
    const [appSettingsLoaded, setAppSettingsLoaded] = useState(!!AppSettings);

    useEffect(() => {
        const initializeApp = async () => {
            // Load AppSettings if not already loaded (for Vite dev server)
            // otherwise it should already be populated through index.html getting rendered with sharpscript
            if (!AppSettings) {
                try {
                    const { appSettingsApi } = await import('./services/appSettings');
                    const response = await appSettingsApi.getAppSettingsPromise();

                    if (response) {
                        // Handle both response.result and direct response
                        const settings = (response as any).result || response;
                        setAppSettings(settings);
                        setAppSettingsLoaded(true);
                        
                        // Initialize Sentry after AppSettings are loaded (dev mode only)
                        if (settings && settings.sentry?.dsn) {
                            try {
                                initializeSentry();
                            } catch (error) {
                                console.error('[Sentry] Failed to initialize:', error);
                            }
                        }
                    }
                } catch (error) {
                    console.error('[App] Failed to load app settings:', error);
                    // Set loaded anyway to prevent infinite loading
                    setAppSettingsLoaded(true);
                }
            }
        };

        initializeApp();
    }, []);

    if (!appSettingsLoaded) {
        return <OverlayLoading />;
    }

    return (
        <div>
            <RouterProvider router={router} />
            <Alert
                stack={{ limit: 5 }}
                html={false}
                effect="scale"
                position="bottom-right"
                timeout={5000}
            />
        </div>
    );
};

ReactDOM.render(
    <React.StrictMode>
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
            <Provider store={store as any}>
                <AuthenticatedUserProvider>
                    <App />
                </AuthenticatedUserProvider>
            </Provider>
        </Sentry.ErrorBoundary>
    </React.StrictMode>,
    document.getElementById("app")
);